import React, { useState, useEffect } from 'react';
import { OverlayView } from '@react-google-maps/api';

// Keep track of the open overlay so only one is shown at a time
let currentlyOpenOverlay = null;

function AdvancedMarker({
  map,
  position,
  category = 'destinations', // default if none given
  name,
  address,
  description,
  website,
  categoryIcons
}) {
  const [marker, setMarker] = useState(null);
  const [showOverlay, setShowOverlay] = useState(false);

  useEffect(() => {
    if (!map || !window.google) return;

    let createdMarker = null;
    if (window.google.maps.importLibrary) {
      // Using the new marker library
      window.google.maps.importLibrary('marker').then(({ AdvancedMarkerElement }) => {
        // Create custom marker DOM
        const contentDiv = document.createElement('div');
        contentDiv.className = 'custom-marker-container';
        contentDiv.style.width = '32px';
        contentDiv.style.height = '32px';
        contentDiv.style.position = 'relative';

        // Create the <img> icon
        const img = document.createElement('img');
        img.src = categoryIcons[category] || categoryIcons.destinations;
        img.alt = name;
        img.style.position = 'absolute';
        img.style.bottom = '0';
        img.style.left = '50%';
        img.style.transform = 'translateX(-50%)';
        img.style.width = '32px';
        img.style.height = '32px';
        img.style.cursor = 'pointer';
        contentDiv.appendChild(img);

        // Create the AdvancedMarkerElement
        createdMarker = new AdvancedMarkerElement({
          map,
          position,
          title: name,
          content: contentDiv,
        });

        // When this marker is clicked, close any existing overlay and open ours
        createdMarker.addListener('click', () => {
          if (currentlyOpenOverlay) {
            currentlyOpenOverlay();
          }
          setShowOverlay(true);
          currentlyOpenOverlay = () => setShowOverlay(false);
        });
        setMarker(createdMarker);
      });
    } else {
      // Fallback to standard Marker if advanced library not available
      createdMarker = new window.google.maps.Marker({
        map,
        position,
        title: name,
        icon: {
          url: categoryIcons[category] || categoryIcons.destinations,
          scaledSize: new window.google.maps.Size(32, 32),
        },
      });
      createdMarker.addListener('click', () => {
        if (currentlyOpenOverlay) {
          currentlyOpenOverlay();
        }
        setShowOverlay(true);
        currentlyOpenOverlay = () => setShowOverlay(false);
      });
      setMarker(createdMarker);
    }

    // Cleanup on unmount
    return () => {
      if (createdMarker) {
        window.google.maps.event.clearListeners(createdMarker, 'click');
        createdMarker.setMap(null);
      }
    };
  }, [map, position, category, name, address, description, website, categoryIcons]);

  // Build the overlay content
  const renderOverlayContent = () => {
    const parts = [];
    if (name) {
      parts.push(
        <h3 key="name" style={{ marginTop: 0, color: 'blue' }}>
          {name}
        </h3>
      );
    } else {
      console.log("Name not provided; skipping name display.");
    }
    if (address) {
      parts.push(
        <p key="address" style={{ color: 'blue' }}>
          <strong>Address:</strong> {address}
        </p>
      );
      // Add the "Open in Maps" link
      parts.push(
        <p key="openMaps">
          <a
            href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`}
            target="_blank"
            rel="noopener noreferrer"
            style={{ textDecoration: 'underline', color: 'blue' }}
          >
            Open in Maps
          </a>
        </p>
      );
    } else {
      console.log("Address not provided; skipping address display.");
    }
    if (description) {
      parts.push(
        <p key="desc">
          <strong>Description:</strong> {description}
        </p>
      );
    } else {
      console.log("Description not provided; skipping description display.");
    }
    if (website) {
      parts.push(
        <p key="website">
          <strong>Website:</strong>{' '}
          <a
            href={website}
            target="_blank"
            rel="noopener noreferrer"
            style={{ textDecoration: 'underline', color: 'blue' }}
          >
            {website}
          </a>
        </p>
      );
    } else {
      console.log("Website not provided; skipping website display.");
    }
    return parts;
  };

  // Our overlay element with modern styling
  const overlayContentElement = (
    <div
      onClick={() => setShowOverlay(false)}
      style={{
        position: 'relative',
        backgroundColor: '#fff',
        borderRadius: '8px',
        boxShadow: '0 2px 6px rgba(0,0,0,0.15)',
        padding: '8px',
        minWidth: '300px',
        maxWidth: '500px',
        minHeight: '50px',
        maxHeight: '200px',
        overflowY: 'auto',
        whiteSpace: 'normal',
        fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, Segoe UI, Roboto, sans-serif',
        fontSize: '14px',
        cursor: 'pointer'
      }}
    >
      {renderOverlayContent()}
    </div>
  );

  return (
    <>
      {showOverlay && (
        <OverlayView
          position={position}
          mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
          getPixelPositionOffset={(width, height) => ({
            x: -(width / 2),
            y: -height - 10,
          })}
        >
          {overlayContentElement}
        </OverlayView>
      )}
    </>
  );
}

export default AdvancedMarker;
